import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Universal PDF viewer.
 *
 * Usage on WEB (unchanged download flow still works):
 *   1. Fetch the PDF blob as before.
 *   2. Instead of `a.download = ...; a.click()` — call `openPdfInViewer(blob, "INV-0001", navigate)`.
 *
 * Usage on MOBILE shell (Expo WebView):
 *   Same route `/pdf-viewer` renders full-screen inside the WebView. The mobile shell
 *   will detect the PDF blob URL and forward to the native react-native-pdf viewer.
 *
 * We stash the blob URL + label on `window.__pdfInFlight` (no redux/context needed).
 */
export function openPdfInViewer(blob, label, navigate) {
    try {
        const url = URL.createObjectURL(blob);
        window.__pdfInFlight = { url, label, ts: Date.now() };
        navigate(`/pdf-viewer?label=${encodeURIComponent(label || "document")}`);
    } catch (e) {
        toast.error("Could not open PDF viewer");
    }
}

export default function PdfViewer() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const label = params.get("label") || "document";

    // Recover blob URL. If stashed >30s ago, treat it as stale (user refreshed).
    const initial = useMemo(() => {
        const stash = window.__pdfInFlight;
        if (stash && Date.now() - stash.ts < 30_000) return stash.url;
        return null;
    }, []);
    const [src] = useState(initial);
    const [stale] = useState(!initial);

    useEffect(() => {
        // Cleanup blob URL when leaving
        return () => {
            const stash = window.__pdfInFlight;
            if (stash?.url === src) {
                URL.revokeObjectURL(stash.url);
                delete window.__pdfInFlight;
            }
        };
    }, [src]);

    const downloadFile = () => {
        if (!src) return;
        const a = document.createElement("a");
        a.href = src;
        a.download = `${label}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const printFile = () => {
        if (!src) return;
        const w = window.open(src, "_blank");
        if (w) {
            w.addEventListener("load", () => {
                try { w.print(); } catch { /* iOS blocks */ }
            });
        }
    };

    const openInNewTab = () => {
        if (src) window.open(src, "_blank", "noopener");
    };

    if (stale) {
        return (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
                <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-gold">PDF · SESSION EXPIRED</div>
                <h2 className="font-display text-2xl">This preview link is no longer valid.</h2>
                <p className="text-muted-foreground text-[13px] max-w-md text-center">
                    PDF previews are held in memory for 30 seconds. Please go back and click &ldquo;View PDF&rdquo; again.
                </p>
                <Button variant="outline" onClick={() => navigate(-1)} data-testid="pdf-back">
                    <ArrowLeft className="w-4 h-4 mr-1.5" />
                    Back
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-56px)]" data-testid="pdf-viewer">
            {/* Toolbar */}
            <div
                className="flex items-center justify-between px-4 py-2 border-b border-border bg-card"
                style={{ borderRadius: 0 }}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        data-testid="pdf-back"
                        className="shrink-0"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                        Back
                    </Button>
                    <div className="min-w-0">
                        <div className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-gold">PDF PREVIEW</div>
                        <div className="font-display text-lg truncate">{label}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={openInNewTab} data-testid="pdf-open-new-tab">
                        <ExternalLink className="w-4 h-4 mr-1.5" />
                        Open
                    </Button>
                    <Button variant="outline" size="sm" onClick={printFile} data-testid="pdf-print">
                        <Printer className="w-4 h-4 mr-1.5" />
                        Print
                    </Button>
                    <Button size="sm" onClick={downloadFile} data-testid="pdf-download">
                        <Download className="w-4 h-4 mr-1.5" />
                        Download
                    </Button>
                </div>
            </div>

            {/* PDF iframe */}
            <div className="flex-1 bg-muted/30">
                {src && (
                    <iframe
                        src={src}
                        title={label}
                        className="w-full h-full border-0"
                        data-testid="pdf-iframe"
                    />
                )}
            </div>
        </div>
    );
}
